import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import currencyFormatter from '../../../../utils/currencyFormatter';
import { notifyError, notifySuccess } from '../../../../utils/notify';
import oikosApi from '../../../../services/oikos';
import { canEmitNfse, checkPendingNfse, sugerirPacotesKaminoMes } from '../../../../utils/kaminoNfse';

const brDate = (value) => {
  const d = String(value || '').slice(0, 10);
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (d || '-');
};

const periodoDoMes = (mesRef) => {
  const m = moment(mesRef, 'YYYY-MM', true);
  const base = m.isValid() ? m : moment();
  return {
    de: base.clone().startOf('month').format('YYYY-MM-DD'),
    ate: base.clone().add(1, 'month').startOf('month').format('YYYY-MM-DD'),
    rotulo: base.clone().format('MM/YYYY'),
  };
};

const linhaRec = (rec) => (
  <div key={rec.rec_id} style={{ fontSize: 12, marginLeft: 22 }}>
    {rec.ep || ('rec ' + rec.rec_id)}
    {' · '}
    {currencyFormatter.format(Number(rec.valor) || 0)}
    {rec.projeto ? ` · ${rec.projeto}` : ''}
    {' · limite '}
    {brDate(rec.limite)}
    {rec.faturada ? ' · ja faturada' : ''}
    {!rec.liberada ? ' · nao liberada' : ''}
  </div>
);

export default function PacotesKaminoMes({ mesRef, onNeedReload }) {
  const { user } = useSelector((state) => state.auth);
  const periodo = useMemo(() => periodoDoMes(mesRef), [mesRef]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [mapa, setMapa] = useState(null);
  const [marcados, setMarcados] = useState({});

  if (!canEmitNfse(user)) return null;

  const carregar = async () => {
    setOpen(true);
    setLoading(true);
    setMapa(null);
    try {
      const data = await sugerirPacotesKaminoMes(periodo);
      setMapa(data);
      const next = {};
      (data.sugeridos || []).forEach((p) => {
        next[p.kamino_id] = Boolean(p.confiante) && !p.ambiguo;
      });
      setMarcados(next);
    } catch (e) {
      notifyError(e?.message || 'Falha ao mapear as notas da Kamino.');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const fechar = async () => {
    const escolhidos = (mapa?.sugeridos || []).filter((p) => marcados[p.kamino_id]);
    if (!escolhidos.length) {
      notifyError('Marque ao menos um pacote.');
      return;
    }
    const recIds = [];
    escolhidos.forEach((p) => (p.recs || []).forEach((r) => recIds.push(r.rec_id)));
    if (new Set(recIds).size !== recIds.length) {
      notifyError('Uma receita esta em dois pacotes marcados. Desmarque um deles.');
      return;
    }
    const ok = window.confirm(
      `Fechar ${escolhidos.length} pacote(s)? Cada receita recebe o ID da nota na Kamino e o vinculo com a NF. Descricao, valor, tomador, prestador, municipio e codigo de servico permanecem.`
    );
    if (!ok) return;
    setClosing(true);
    const erros = [];
    try {
      for (const pacote of escolhidos) {
        for (const rec of pacote.recs || []) {
          await oikosApi.post('/', {
            action: 'update',
            table: 'rec',
            data: { rec_id: rec.rec_id, kamino_id: pacote.kamino_id },
          });
        }
        try {
          const primeiro = pacote.recs[0];
          await checkPendingNfse({ rec_id: primeiro.rec_id, limit: 1 });
        } catch (e) {
          erros.push(`NFS-e ${pacote.numero || pacote.kamino_id}: ${e?.message || 'falha ao gravar a nota'}`);
        }
      }
      if (erros.length) notifyError(erros[0]);
      else notifySuccess(`${escolhidos.length} pacote(s) fechado(s).`);
      if (onNeedReload) onNeedReload();
      await carregar();
    } catch (e) {
      notifyError(e?.message || 'Falha ao fechar os pacotes.');
    } finally {
      setClosing(false);
    }
  };

  const sugeridos = mapa?.sugeridos || [];
  const fechados = mapa?.fechados || [];
  const semPacote = mapa?.sem_pacote || [];
  const semNota = mapa?.recs_sem_nota || [];

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Button size="small" variant="outlined" onClick={() => { carregar().catch(() => {}); }}>
          Mapear notas do mes
        </Button>
      </div>
      <Dialog open={open} onClose={() => { if (!closing) setOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle>Notas da Kamino em {periodo.rotulo}</DialogTitle>
        <DialogContent>
          <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 12 }}>
            Cruza as NFS-e emitidas neste mes com as receitas pelo CNPJ do cliente e pelo valor.
            Se a receita nao tem tomador, sugere o mesmo valor com limite proximo. Fechar grava so o vinculo.
          </div>
          {loading && <div>Lendo a Kamino...</div>}
          {!loading && mapa && (
            <>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Sugestoes ({sugeridos.length})</div>
              {sugeridos.length === 0 && <div style={{ fontSize: 13, marginBottom: 12 }}>Nenhuma sugestao automatica neste mes. As notas sem pacote mostram a receita mais proxima.</div>}
              {sugeridos.map((p) => (
                <label key={p.kamino_id} style={{ display: 'block', border: '1px solid #e6e8ee', borderRadius: 6, padding: 8, marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(marcados[p.kamino_id])}
                      onChange={(e) => setMarcados((prev) => ({ ...prev, [p.kamino_id]: e.target.checked }))}
                    />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>
                        NFS-e {p.numero || '-'} · ID {p.kamino_id} · {currencyFormatter.format(Number(p.valor) || 0)} · {brDate(p.emissao)}
                      </div>
                      <div style={{ fontSize: 12, color: '#6c757d' }}>{p.tomador}</div>
                      {p.descricao ? <div style={{ fontSize: 12, color: '#6c757d' }}>{p.descricao}</div> : null}
                      <div style={{ fontSize: 12, color: p.ambiguo ? '#b46900' : '#4e515e' }}>{p.motivo}</div>
                      {(p.recs || []).map(linhaRec)}
                    </div>
                  </div>
                </label>
              ))}

              <div style={{ fontWeight: 800, margin: '12px 0 6px' }}>Ja vinculadas ({fechados.length})</div>
              {fechados.map((p) => (
                <div key={'f' + p.kamino_id} style={{ fontSize: 12, marginBottom: 8 }}>
                  <div>NFS-e {p.numero || '-'} · ID {p.kamino_id} · {currencyFormatter.format(Number(p.valor) || 0)}</div>
                  {(p.recs || []).map(linhaRec)}
                </div>
              ))}

              <div style={{ fontWeight: 800, margin: '12px 0 6px' }}>Notas sem pacote ({semPacote.length})</div>
              {semPacote.map((p) => (
                <div key={'s' + p.kamino_id} style={{ fontSize: 12, marginBottom: 8 }}>
                  <div>NFS-e {p.numero || '-'} · {currencyFormatter.format(Number(p.valor) || 0)} · {p.tomador}</div>
                  {p.descricao ? <div style={{ color: '#6c757d' }}>{p.descricao}</div> : null}
                  <div style={{ color: '#4e515e' }}>{p.motivo}</div>
                  {(p.proximas || []).map((rec) => (
                    <div key={rec.rec_id} style={{ marginLeft: 12 }}>
                      {rec.ep || ('rec ' + rec.rec_id)}
                      {' · '}
                      {currencyFormatter.format(Number(rec.valor) || 0)}
                      {' · limite '}
                      {brDate(rec.limite)}
                      {' · '}
                      {rec.porque}
                    </div>
                  ))}
                </div>
              ))}

              <div style={{ fontWeight: 800, margin: '12px 0 6px' }}>Receitas do mes sem nota sugerida ({semNota.length})</div>
              {semNota.slice(0, 30).map(linhaRec)}
              {semNota.length > 30 && <div style={{ fontSize: 12, color: '#6c757d' }}>E mais {semNota.length - 30}.</div>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={closing}>Fechar</Button>
          <Button variant="contained" onClick={() => { fechar().catch(() => {}); }} disabled={closing || loading || sugeridos.length === 0}>
            {closing ? 'Fechando...' : 'Fechar pacotes marcados'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
